import ModalNames from '@/constants/ModalNames';
import RouteNames from '@/constants/RouteNames';
import type { MovementType } from '@/types/movement';

export default {
  auth: {
    callback: {
      processing: "Traitement de l'authentification...",
    },
    errors: {
      ACCESS_DENIED: "Accès refusé. Vous n'êtes pas autorisé à accéder à cette application.",
    },
    login: {
      devMode: 'Connexion de développement',
      email: 'Courriel',
      password: 'Mot de passe',
      submit: 'Connexion',
      title: 'Connexion',
      withProvider: 'Connexion avec {provider}',
    },
    providers: {
      keycloak: 'Keycloak',
      microsoft: 'Microsoft',
    },
  },
  categories: {
    sync: {
      button: 'Sync. catégories',
      success: 'Catégories synchronisées avec succès.',
    },
  },
  core: {
    appName: "Gestion d'inventaire",
    cancel: 'Annuler',
    clear: 'Effacer',
    confirm: 'Confirmer',
    confirmChanges: 'Confirmer les modifications',
    create: 'Créer',
    delete: 'Supprimer',
    next: 'Suivant',
    pageOf: 'Page {current} sur {total}',
    previous: 'Précédent',
    restore: 'Restaurer',
    save: 'Enregistrer',
    selectValue: 'Sélectionner...',
    update: 'Mettre à jour',
  },
  employees: {
    columns: {
      employee: 'Employé',
      lastActivity: 'Dernière activité',
      pin: 'PIN',
      role: 'Rôle',
      status: 'Statut',
    },
    createSuccess: 'Employé créé avec succès.',
    deleteSuccess: 'Employé supprimé avec succès.',
    filters: {
      allRoles: 'Tous les rôles',
      role: 'Rôle',
      showDeleted: 'Afficher les utilisateurs inactifs',
    },
    form: {
      createTitle: 'Nouvel employé',
      editTitle: 'Modifier employé',
      email: 'Courriel',
      emailPlaceholder: 'exemple@entreprise.com',
      errors: {
        emailInvalid: 'Format de courriel invalide.',
        emailRequired: 'Le courriel est requis.',
        firstNameRequired: 'Le prénom est requis.',
        lastNameRequired: 'Le nom est requis.',
        pinAlreadyExists: 'Ce code PIN est déjà utilisé par un autre employé.',
        pinInvalid: 'Le PIN doit contenir exactement 6 chiffres.',
        roleRequired: 'Le rôle est requis.',
      },
      firstName: 'Prénom',
      firstNamePlaceholder: 'Entrez le prénom',
      lastName: 'Nom',
      lastNamePlaceholder: 'Entrez le nom',
      pin: 'Code PIN',
      pinHelp: 'Code PIN optionnel de 6 chiffres pour la connexion rapide',
      pinPlaceholder: '123456',
      role: 'Rôle',
      selectRole: 'Sélectionner un rôle',
    },
    noActivity: 'Aucune activité',
    noEmail: 'Aucun courriel',
    noResults: 'Aucun employé trouvé.',
    noRole: 'Aucun rôle',
    restoreSuccess: 'Employé restauré avec succès.',
    search: {
      label: 'Recherche',
      placeholder: 'Prénom, nom ou email...',
    },
    status: {
      active: 'Actif',
      deleted: 'Supprimé',
      inactive: 'Inactif',
    },
    title: 'Paramètres des employés',
    updateSuccess: 'Employé mis à jour avec succès.',
  },
  errors: {
    // Generic error messages for fallback
    DUPLICATE_KEY_VIOLATION: 'Cette valeur existe déjà dans le système.',
    FOREIGN_KEY_VIOLATION: 'Référence invalide. Veuillez vérifier les données sélectionnées.',
    GENERIC_ERROR: 'Une erreur inattendue est survenue. Veuillez réessayer.',
    INTERNAL_SERVER_ERROR: 'Une erreur inattendue est survenue. Veuillez réessayer.',
    METHOD_NOT_ALLOWED: 'Méthode non autorisée.',
    PERMISSION_DENIED: "Vous n'avez pas les permissions nécessaires pour cette action.",
    UNAUTHORIZED: "Vous n'êtes pas autorisé à accéder à cette ressource.",
    UNKNOWN_ERROR: 'Erreur inconnue. Veuillez contacter le support technique.',

    createUser: {
      CALLER_NOT_ADMIN: "Vous n'avez pas les privilèges d'administrateur.",
      EMAIL_REQUIRED: "L'adresse courriel est requise.",
      EMPLOYEE_UPDATE_FAILED: "Échec de la mise à jour de l'employé.",
      INTERNAL_SERVER_ERROR: 'Une erreur inattendue est survenue. Veuillez réessayer.',
      INVALID_EMAIL_FORMAT: "Le format de l'adresse courriel est invalide.",
      METHOD_NOT_ALLOWED: 'Méthode non autorisée.',
      UNAUTHORIZED: "Vous n'êtes pas autorisé à accéder à cette ressource.",
      USER_CREATION_FAILED: "Échec de la création de l'utilisateur.",
      USER_NOT_ADMIN: "L'utilisateur n'a pas les privilèges d'administrateur.",
    },
    employee: {
      AUTHENTICATION_REQUIRED: 'Authentification requise.',
      EMAIL_ALREADY_EXISTS: 'Cette adresse courriel est déjà utilisée par un autre employé.',
      EMPLOYEE_DELETE_FAILED_CANNOT_DELETE_SELF: 'Impossible de supprimer votre propre compte. Un autre administrateur doit effectuer cette action.',
      EMPLOYEE_DELETE_FAILED_EMPLOYEE_NOT_FOUND: 'Employé introuvable ou déjà supprimé.',
      EMPLOYEE_DELETE_FAILED_HAS_ACTIVE_MOVEMENTS: 'Impossible de supprimer un employé qui a des mouvements actifs.',
      EMPLOYEE_DELETE_FAILED_INSUFFICIENT_PERMISSIONS: "Vous n'avez pas les permissions nécessaires pour supprimer un employé.",
      EMPLOYEE_DELETE_FAILED_INVALID_EMPLOYEE_UID: "Référence d'employé invalide.",
      EMPLOYEE_DELETE_FAILED_INVALID_EMPLOYEE_UID_FORMAT: "L'identifiant de l'employé est invalide.",
      EMPLOYEE_NOT_FOUND_WITH_PIN: 'Code PIN invalide.',
      EMPLOYEE_RESTORE_FAILED_EMPLOYEE_NOT_FOUND_OR_NOT_DELETED: 'Employé introuvable ou non supprimé.',
      EMPLOYEE_RESTORE_FAILED_INVALID_EMPLOYEE_UID: "Référence d'employé invalide.",
      EMPLOYEE_RESTORE_FAILED_INVALID_EMPLOYEE_UID_FORMAT: "L'identifiant de l'employé est invalide.",
      EMPLOYEE_UPDATE_FAILED_CANNOT_CHANGE_OWN_ROLE: 'Impossible de modifier votre propre rôle. Un autre administrateur doit effectuer cette action.',
      ROLE_REQUIRED: 'Le rôle est requis.',
    },
    foreman: {
      FOREMAN_CREATION_FAILED: 'Échec de la création du contremaître.',
      FOREMAN_CREATION_FAILED_COMPANY_NOT_FOUND: "La compagnie sélectionnée n'existe pas.",
      FOREMAN_CREATION_FAILED_INVALID_COMPANY: 'La compagnie est invalide.',
      FOREMAN_CREATION_FAILED_INVALID_COMPANY_FORMAT: 'La compagnie est invalide.',
      FOREMAN_CREATION_FAILED_INVALID_FIRST_NAME: 'Le prénom est invalide.',
      FOREMAN_CREATION_FAILED_INVALID_LAST_NAME: 'Le nom est invalide.',
      FOREMAN_CREATION_FAILED_INVALID_STATUS: 'Le statut est invalide.',
      FOREMAN_DELETE_FAILED_FOREMAN_NOT_FOUND: 'Contremaître introuvable.',
      FOREMAN_DELETE_FAILED_INVALID_FOREMAN_UID: 'Référence de contremaître invalide.',
      FOREMAN_DELETE_FAILED_INVALID_FOREMAN_UID_FORMAT: 'Le format de la référence de contremaître est invalide.',
      FOREMAN_NOT_FOUND: 'Contremaître introuvable.',
      FOREMAN_UPDATE_FAILED: 'Échec de la mise à jour du contremaître.',
      FOREMAN_UPDATE_FAILED_COMPANY_NOT_FOUND: "La compagnie sélectionnée n'existe pas.",
      FOREMAN_UPDATE_FAILED_INVALID_COMPANY: 'La compagnie est invalide.',
      FOREMAN_UPDATE_FAILED_INVALID_COMPANY_FORMAT: 'Le format de la compagnie est invalide.',
      FOREMAN_UPDATE_FAILED_INVALID_FIRST_NAME: 'Le prénom est invalide.',
      FOREMAN_UPDATE_FAILED_INVALID_FOREMAN_UID: 'Référence de contremaître invalide.',
      FOREMAN_UPDATE_FAILED_INVALID_FOREMAN_UID_FORMAT: 'Le format de la référence de contremaître est invalide.',
      FOREMAN_UPDATE_FAILED_INVALID_LAST_NAME: 'Le nom est invalide.',
      FOREMAN_UPDATE_FAILED_INVALID_STATUS: 'Le statut est invalide.',
    },
    material: {
      SYNC_HARDWARES_FAILED: 'Échec de la synchronisation des matériaux.',
    },
    movement: {
      INTERNAL_SERVER_ERROR: 'Une erreur inattendue est survenue. Veuillez réessayer.',
      MOVEMENT_CREATION_FAILED: 'Échec de la création du mouvement.',
      MOVEMENT_CREATION_FAILED_EMPLOYEE_NOT_FOUND: "L'employé sélectionné n'existe pas.",
      MOVEMENT_CREATION_FAILED_FOREMAN_NOT_FOUND: "Le contremaître sélectionné n'existe pas.",
      MOVEMENT_CREATION_FAILED_INVALID_DATE: 'Veuillez entrer une date valide.',
      MOVEMENT_CREATION_FAILED_INVALID_DATE_FORMAT: 'Le format de date est invalide. Veuillez utiliser AAAA-MM-JJ.',
      MOVEMENT_CREATION_FAILED_INVALID_EMPLOYEE: 'Veuillez sélectionner un employé.',
      MOVEMENT_CREATION_FAILED_INVALID_EMPLOYEE_FORMAT: "L'identifiant de l'employé est invalide.",
      MOVEMENT_CREATION_FAILED_INVALID_FOREMAN: 'Veuillez sélectionner un contremaître.',
      MOVEMENT_CREATION_FAILED_INVALID_FOREMAN_FORMAT: "L'identifiant du contremaître est invalide.",
      MOVEMENT_CREATION_FAILED_INVALID_PROJECT: 'Veuillez sélectionner un projet.',
      MOVEMENT_CREATION_FAILED_INVALID_PROJECT_FORMAT: "L'identifiant du projet est invalide.",
      MOVEMENT_CREATION_FAILED_INVALID_TYPE: 'Le type de mouvement est invalide.',
      MOVEMENT_CREATION_FAILED_INVALID_TYPE_VALUE: 'Le type de mouvement est invalide.',
      MOVEMENT_CREATION_FAILED_PROJECT_NOT_FOUND: "Le projet sélectionné n'existe pas.",
      MOVEMENT_DELETE_FAILED_DATE_CANNOT_BE_IN_PAST: 'Impossible de supprimer un mouvement dans le passé.',
      MOVEMENT_DELETE_FAILED_INVALID_MOVEMENT_UID: 'Référence de mouvement invalide.',
      MOVEMENT_DELETE_FAILED_INVALID_MOVEMENT_UID_FORMAT: "L'identifiant du mouvement est invalide.",
      MOVEMENT_DELETE_FAILED_MOVEMENT_NOT_FOUND: 'Mouvement introuvable.',
      MOVEMENT_HARDWARE_CREATION_FAILED: 'Échec de la création des articles de matériel.',
      MOVEMENT_HARDWARE_CREATION_FAILED_HARDWARE_NOT_FOUND: "L'article de matériel sélectionné n'existe pas.",
      MOVEMENT_HARDWARE_CREATION_FAILED_INVALID_HARDWARE: 'Veuillez sélectionner un article de matériel valide.',
      MOVEMENT_HARDWARE_CREATION_FAILED_INVALID_HARDWARE_FORMAT: "L'identifiant de l'article de matériel est invalide.",
      MOVEMENT_HARDWARE_CREATION_FAILED_INVALID_QUANTITY: 'Veuillez entrer une quantité valide.',
      MOVEMENT_HARDWARE_CREATION_FAILED_NO_HARDWARE_ITEMS: 'Veuillez ajouter au moins un article de matériel.',
      MOVEMENT_HARDWARE_DELETE_FAILED: 'Échec de la suppression des articles de matériel.',
      MOVEMENT_HARDWARE_INVALID_DATA: 'Les données des articles de matériel sont invalides.',
      MOVEMENT_NOT_FOUND: 'Mouvement introuvable.',
      MOVEMENT_UPDATE_FAILED: 'Échec de la mise à jour du mouvement.',
      MOVEMENT_UPDATE_FAILED_DATE_CANNOT_BE_IN_PAST: 'Impossible de modifier un mouvement dans le passé.',
      MOVEMENT_UPDATE_FAILED_EMPLOYEE_NOT_FOUND: "L'employé sélectionné n'existe pas.",
      MOVEMENT_UPDATE_FAILED_FOREMAN_NOT_FOUND: "Le contremaître sélectionné n'existe pas.",
      MOVEMENT_UPDATE_FAILED_INVALID_DATE: 'Veuillez entrer une date valide.',
      MOVEMENT_UPDATE_FAILED_INVALID_DATE_FORMAT: 'Le format de date est invalide. Veuillez utiliser AAAA-MM-JJ.',
      MOVEMENT_UPDATE_FAILED_INVALID_EMPLOYEE: 'Veuillez sélectionner un employé.',
      MOVEMENT_UPDATE_FAILED_INVALID_EMPLOYEE_FORMAT: "L'identifiant de l'employé est invalide.",
      MOVEMENT_UPDATE_FAILED_INVALID_FOREMAN: 'Veuillez sélectionner un contremaître.',
      MOVEMENT_UPDATE_FAILED_INVALID_FOREMAN_FORMAT: "L'identifiant du contremaître est invalide.",
      MOVEMENT_UPDATE_FAILED_INVALID_MOVEMENT_UID: 'Référence de mouvement invalide.',
      MOVEMENT_UPDATE_FAILED_INVALID_MOVEMENT_UID_FORMAT: "L'identifiant du mouvement est invalide.",
      MOVEMENT_UPDATE_FAILED_INVALID_PROJECT: 'Veuillez sélectionner un projet.',
      MOVEMENT_UPDATE_FAILED_INVALID_PROJECT_FORMAT: "L'identifiant du projet est invalide.",
      MOVEMENT_UPDATE_FAILED_PROJECT_NOT_FOUND: "Le projet sélectionné n'existe pas.",
      TRANSACTION_BEGIN_FAILED: 'Échec du démarrage de la transaction.',
      TRANSACTION_COMMIT_FAILED: 'Échec de la validation de la transaction.',
      TRANSACTION_ROLLBACK_FAILED: "Échec de l'annulation de la transaction.",
      TRANSACTION_TIMEOUT: "L'opération a expiré. Veuillez réessayer.",
    },
    project: {
      SYNC_PROJECTS_FAILED: 'Échec de la synchronisation des projets.',
    },
    userProfile: {
      EMAIL_NOT_FOUND: 'Aucun utilisateur trouvé avec cette adresse courriel.',
      UNAUTHORIZED: "Vous n'êtes pas autorisé à accéder à cette ressource.",
      USER_PROFILE_NOT_FOUND: "Profil d'utilisateur introuvable.",
    },
  },
  foremans: {
    columns: {
      company: 'Compagnie',
      email: 'Courriel',
      firstName: 'Prénom',
      lastName: 'Nom',
      phone: 'Téléphone',
      status: 'Statut',
    },
    confirmDelete: 'Êtes-vous certain de vouloir supprimer le contremaître {fullName}?',
    deleted: 'Le contremaître {fullName} a été supprimé.',
    filters: {
      includeInactive: 'Inclure les inactifs',
      search: 'Recherche',
    },
    noResults: 'Aucun contremaître trouvé.',
    saveSuccess: 'Le contremaître {fullName} a été enregistré avec succès.',
    status: {
      active: 'Actif',
      inactive: 'Inactif',
    },
    titles: {
      configuration: 'Configuration',
      personal: 'Informations personnelles',
    },
  },
  hardwares: {
    allCategories: 'Toutes les catégories',
    category: {
      label: 'Catégorie',
    },
    columns: {
      category: 'Catégorie',
      cost: 'Coût',
      description: 'Description',
      id: 'Code',
      lastPurchaseDate: 'Dernier achat',
      minimum: 'Minimum',
      subcategory: 'Sous-catégorie',
      subcategoryShort: 'S-catégorie',
    },
    noResults: 'Aucun matériau trouvé.',
    search: {
      label: 'Recherche',
      placeholder: 'Rechercher par code ou description...',
    },
    sync: {
      button: 'Sync. matériaux',
      success: '{count} matériaux synchronisés avec succès.',
    },
    title: 'Matériaux',
  },
  modalNames: {
    [ModalNames.PinLogin]: 'Entrer votre code',
    [ModalNames.Foreman.Delete]: 'Supprimer un contremaître',
    [ModalNames.Movement.Delete]: 'Supprimer {movementId}',
  },
  movements: {
    addHardware: 'Ajouter des matériaux',
    categories: {
      base: 'Inventaire de base',
      others: 'Autres',
    },
    confirmDelete: 'Êtes-vous certain de vouloir supprimer le mouvement?',
    date: 'Date',
    dateNotSelected: 'Date manquante',
    deleted: 'Le mouvement {movementId} a été supprimé.',
    employee: 'Préparé par',
    filterInCategories: 'Seulement inventaire de base',
    foreman: 'Contremaître',
    hardwareAvailable: 'Matériaux disponibles',
    hardwareCodeOrDescription: 'Code / Descr.',
    hardwareMoved: 'Matériaux déplacés',
    hardwareSubCategory: 'Sous-catégorie',
    hardwareSummary: 'Sommaire du mouvement',
    movementCompleted: 'Mouvement terminé',
    movementId: 'Code',
    noHardwareFound: 'Aucun matériel trouvé',
    noHardwareFoundAddFilters: 'Aucun matériel trouvé, ajoutez des filtres pour affiner la recherche.',
    noHardwareMoved: 'Aucun matériel déplacé',
    noResults: 'Aucun mouvement pour la période.',
    period: 'Période',
    project: 'Projet / Adresse',
    projectNotSelected: 'Projet manquant',
    quantity: 'Quantité',
    searchPlaceholder: 'Recherche',
    today: "Aujourd'hui",
    type: 'Type',
    types: {
      IN: 'Retour',
      OUT: 'Sortie',
    } as Record<MovementType, string>,
    validations: {
      atLeastOneHardware: 'Matériaux',
      dateIsValid: 'Date invalide',
      projectIsRequired: 'Projet',
      typeIsRequired: 'Type manquant',
    },
    yesterday: 'Hier',
  },
  projects: {
    address: 'Adresse',
    allCompanies: 'Toutes les compagnies',
    columns: {
      company: 'Compagnie',
      id: 'Code',
      name: 'Adresse / Description',
      status: 'Statut',
    },
    company: {
      label: 'Compagnie',
    },
    description: 'Adresse',
    id: 'ID du projet',
    noResults: 'Aucun projet trouvé.',
    projectId: 'Projet',
    search: {
      label: 'Recherche',
      placeholder: 'Rechercher par ID ou nom...',
    },
    status: {
      active: 'Actif',
      activeOnly: 'Actifs seulement',
      inactive: 'Inactif',
      includeInactive: 'Inclure inactifs',
      label: 'Statut',
    },
    sync: {
      button: 'Synchroniser',
      success: '{count} projets synchronisés avec succès.',
    },
    title: 'Projets',
  },
  roles: {
    admin: 'Administrateur',
    authenticated: 'Entrepôt',
    logistic: 'Logistique',
    unknown: 'Rôle inconnu',
    user: 'Utilisateur',
  },
  routeNames: {
    [RouteNames.Login]: 'Connexion',
    [RouteNames.Employee.Create]: 'Nouvel employé',
    [RouteNames.Employee.Edit]: 'Modifier un employé',
    [RouteNames.Employee.List]: 'Employés',
    [RouteNames.Foreman.Create]: 'Nouveau contremaître',
    [RouteNames.Foreman.Edit]: 'Modifier le contremaître',
    [RouteNames.Foreman.List]: 'Contremaîtres',
    [RouteNames.Hardware.List]: 'Matériaux',
    [RouteNames.Movement.List]: 'Mouvements',
    [RouteNames.Movement.Create]: 'Nouveau mouvement',
    [RouteNames.Movement.Edit + 'IN']: 'Retour',
    [RouteNames.Movement.Edit + 'OUT']: 'Sortie',
    [RouteNames.Project.List]: 'Projets',
  },
};
